import React, { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { DataProvider } from "@/context/DataContext";
import { CartProvider } from "@/context/CartContext";
import { MerchCartProvider } from "@/context/MerchCartContext";
import { BusinessStatusProvider } from "@/context/BusinessStatusContext";
import { AuthProvider } from "@/context/AuthContext";
import { api } from "@/lib/api";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import StickyOrderBar from "@/components/StickyOrderBar";
import ChatWidget from "@/components/ChatWidget";
import CartDrawer from "@/components/CartDrawer";
import MerchCartDrawer from "@/components/MerchCartDrawer";
import SiteStatusBar from "@/components/SiteStatusBar";
import ErrorBoundary from "@/components/ErrorBoundary";

import Home from "@/pages/Home";
import MenuPage from "@/pages/MenuPage";
import CateringPage from "@/pages/CateringPage";
import StoryPage from "@/pages/StoryPage";
import DeliveryPickupPage from "@/pages/DeliveryPickupPage";
import ContactPage from "@/pages/ContactPage";
import TrackOrderPage from "@/pages/TrackOrderPage";
import PaymentResult from "@/pages/PaymentResult";
import AdminDashboard from "@/pages/AdminDashboard";
import AccountPage from "@/pages/AccountPage";
import CareersPage from "@/pages/CareersPage";
import MerchPage from "@/pages/MerchPage";
import MerchReturnsPage from "@/pages/MerchReturnsPage";

const AuthCallback = () => {
  const navigate = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        if (sid) await api.post("/auth/session", { session_id: sid });
      } catch (e) {
        console.error(e);
      } finally {
        const target = window.location.pathname || "/admin";
        window.history.replaceState(null, "", target);
        navigate(target, { replace: true });
        window.location.reload();
      }
    })();
  }, [navigate]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#101311] text-amber-400 font-display text-2xl">
      Signing you in…
    </div>
  );
};

const Layout = ({ children }) => (
  <>
    <Header />
    <SiteStatusBar />
    <main className="min-h-screen pb-[calc(6.5rem+env(safe-area-inset-bottom))] md:pb-0">{children}</main>
    <Footer />
    <StickyOrderBar />
    <ChatWidget />
    <CartDrawer />
    <MerchCartDrawer />
  </>
);

const ScrollTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <ScrollTop />
      <Routes>
        <Route path="/" element={<Layout><Home /></Layout>} />
        <Route path="/menu" element={<Layout><MenuPage /></Layout>} />
        <Route path="/order" element={<Layout><MenuPage /></Layout>} />
        <Route path="/catering" element={<Layout><CateringPage /></Layout>} />
        <Route path="/story" element={<Layout><StoryPage /></Layout>} />
        <Route path="/delivery-pickup" element={<Layout><DeliveryPickupPage /></Layout>} />
        <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
        <Route path="/track" element={<Layout><TrackOrderPage /></Layout>} />
        <Route path="/payment/success" element={<Layout><PaymentResult /></Layout>} />
        <Route path="/payment/cancel" element={<Layout><PaymentResult /></Layout>} />
        <Route path="/account" element={<Layout><AccountPage /></Layout>} />
        <Route path="/careers" element={<Layout><CareersPage /></Layout>} />
        <Route path="/merch" element={<Layout><MerchPage /></Layout>} />
        <Route path="/merch/returns" element={<Layout><MerchReturnsPage /></Layout>} />
        <Route path="/admin" element={<AdminDashboard />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App bg-[#101311] text-[#FDFBF7]">
      <BrowserRouter>
        <AuthProvider>
          <DataProvider>
            <BusinessStatusProvider>
              <CartProvider>
                <MerchCartProvider>
                  <ErrorBoundary>
                    <AppRouter />
                  </ErrorBoundary>
                  <Toaster position="top-center" theme="dark" richColors />
                </MerchCartProvider>
              </CartProvider>
            </BusinessStatusProvider>
          </DataProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
